import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { Switch } from "@/components/ui/switch";
import { useGroupData } from "@/data/use-groups-data";
import { useSettingData } from "@/data/use-settings-data";
import { useParams } from "@tanstack/react-router";
import { toast } from "sonner";

export default function GlobalAdminGroupFeatureFlagsPage() {
  const { groupUuid } = useParams({ strict: false }) as {
    groupUuid?: string;
  };
  const { group } = useGroupData(groupUuid);

  const {
    value: multipleExpertsFeatureFlag,
    setValue: setMultipleExpertsFeatureFlag,
  } = useSettingData(groupUuid, "FEATURE_FLAG_MULTIPLE_EXPERTS_CHAT");
  const { value: researchFeatureFlag, setValue: setResearchFeatureFlag } =
    useSettingData(groupUuid, "FEATURE_FLAG_RESEARCHER");
  const { value: interviewerFeatureFlag, setValue: setInterviewerFeatureFlag } =
    useSettingData(groupUuid, "FEATURE_FLAG_INTERVIEWER");
  const {
    value: advancedModelsFeatureFlag,
    setValue: setadvancedModelsFeatureFlag,
  } = useSettingData(groupUuid, "FEATURE_FLAG_ADVANCED_MODELS");
  const {
    value: toolAutoApproveFeatureFlag,
    setValue: setToolAutoApproveFeatureFlag,
  } = useSettingData(groupUuid, "FEATURE_FLAG_TOOL_AUTO_APPROVE");
  const {
    value: sharePointAutoSyncFeatureFlag,
    setValue: setSharePointAutoSyncFeatureFlag,
  } = useSettingData(groupUuid, "FEATURE_FLAG_SHAREPOINT_AUTO_SYNC");
  const {
    value: fileNodeTreeFeatureFlag,
    setValue: setFileNodeTreeFeatureFlag,
  } = useSettingData(groupUuid, "FEATURE_FLAG_FILE_NODE_TREE");
  const {
    value: salthubAttachFeatureFlag,
    setValue: setSalthubAttachFeatureFlag,
  } = useSettingData(groupUuid, "FEATURE_FLAG_SALTHUB_ATTACH");

  const handleToggleMultipleExpertsFeatureFlag = async () => {
    try {
      await setMultipleExpertsFeatureFlag(
        multipleExpertsFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: Multiple Experts Chat.");
    } catch {
      toast.error("Failed to update feature flag: Multiple Experts Chat.");
    }
  };

  const handleToggleResearchFeatureFlag = async () => {
    try {
      await setResearchFeatureFlag(
        researchFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: Research Feature.");
    } catch {
      toast.error("Failed to update feature flag: Research Feature.");
    }
  };

  const handleToggleInterviewerFeatureFlag = async () => {
    try {
      await setInterviewerFeatureFlag(
        interviewerFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: Interviewer.");
    } catch {
      toast.error("Failed to update feature flag: Interviewer.");
    }
  };

  const handleToggleadvancedModelsFeatureFlag = async () => {
    try {
      await setadvancedModelsFeatureFlag(
        advancedModelsFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: Advanced Models.");
    } catch {
      toast.error("Failed to update feature flag: Advanced Models.");
    }
  };

  const handleToggleToolAutoApproveFeatureFlag = async () => {
    try {
      await setToolAutoApproveFeatureFlag(
        toolAutoApproveFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: Tool Auto-Approve.");
    } catch {
      toast.error("Failed to update feature flag: Tool Auto-Approve.");
    }
  };

  const handleToggleSharePointAutoSyncFeatureFlag = async () => {
    try {
      await setSharePointAutoSyncFeatureFlag(
        sharePointAutoSyncFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: SharePoint Auto-Sync.");
    } catch {
      toast.error("Failed to update feature flag: SharePoint Auto-Sync.");
    }
  };

  const handleToggleFileNodeTreeFeatureFlag = async () => {
    try {
      await setFileNodeTreeFeatureFlag(
        fileNodeTreeFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: File Node Tree.");
    } catch {
      toast.error("Failed to update feature flag: File Node Tree.");
    }
  };

  const handleToggleSalthubAttachFeatureFlag = async () => {
    try {
      await setSalthubAttachFeatureFlag(
        salthubAttachFeatureFlag === "enabled" ? "disabled" : "enabled",
      );
      toast.success("Feature flag updated: SaltHub Attach.");
    } catch {
      toast.error("Failed to update feature flag: SaltHub Attach.");
    }
  };

  if (!group) return null;

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Feature Flags</PageHeader.Title>
      </PageHeader>
      <PageBody innerClassName="gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="multiple-experts-chat-feature-flag"
              onCheckedChange={handleToggleMultipleExpertsFeatureFlag}
              checked={multipleExpertsFeatureFlag === "enabled"}
            />
            <label
              htmlFor="multiple-experts-chat-feature-flag"
              className="select-none"
            >
              Multiple Experts Chat
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="research-feature-flag"
              onCheckedChange={handleToggleResearchFeatureFlag}
              checked={researchFeatureFlag === "enabled"}
            />
            <label htmlFor="research-feature-flag" className="select-none">
              Research Feature
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="interviewer-feature-flag"
              onCheckedChange={handleToggleInterviewerFeatureFlag}
              checked={interviewerFeatureFlag === "enabled"}
            />
            <label htmlFor="interviewer-feature-flag" className="select-none">
              Interviewer
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="claude-feature-flag"
              onCheckedChange={handleToggleadvancedModelsFeatureFlag}
              checked={advancedModelsFeatureFlag === "enabled"}
            />
            <label htmlFor="claude-feature-flag" className="select-none">
              Advanced Models (e.g. Claude Opus)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="tool-auto-approve-feature-flag"
              onCheckedChange={handleToggleToolAutoApproveFeatureFlag}
              checked={toolAutoApproveFeatureFlag === "enabled"}
            />
            <label
              htmlFor="tool-auto-approve-feature-flag"
              className="select-none"
            >
              Tool Auto-Approve (skip HITL for activate skill)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="sharepoint-auto-sync-feature-flag"
              onCheckedChange={handleToggleSharePointAutoSyncFeatureFlag}
              checked={sharePointAutoSyncFeatureFlag === "enabled"}
            />
            <label
              htmlFor="sharepoint-auto-sync-feature-flag"
              className="select-none"
            >
              SharePoint Auto-Sync (Expert knowledge)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="file-node-tree-feature-flag"
              onCheckedChange={handleToggleFileNodeTreeFeatureFlag}
              checked={fileNodeTreeFeatureFlag === "enabled"}
            />
            <label
              htmlFor="file-node-tree-feature-flag"
              className="select-none"
            >
              File Node Tree
            </label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="salthub-attach-feature-flag"
              onCheckedChange={handleToggleSalthubAttachFeatureFlag}
              checked={salthubAttachFeatureFlag === "enabled"}
            />
            <label
              htmlFor="salthub-attach-feature-flag"
              className="select-none"
            >
              SaltHub Attach (add new users by SaltHub client ID)
            </label>
          </div>
        </div>
      </PageBody>
    </Page>
  );
}
